import React, { useEffect, useRef, useState } from "react";
import './Map.scss';
import './Adaptations.scss';
import { MapContextProvider } from "../../contexts/MapContext";
import Clouds from "./clouds/Clouds";
import ZoomButton from "./zoom-button/ZoomButton";
import VisibilitySelect from "./visibility-select/VisibilitySelect";
import DistancesSwitch from "./distances-switch/DistancesSwitch";
import MarkersMapper from "./markers/markers-mapper/MarkersMapper";
import MarkersPath from "./markers/markers-path/MarkersPath";
import MarkersPathLoader from "./markers/markers-path/loader/Loader";
import MarkersPathWindow from "./markers/markers-path/window/MarkersPathWindow";
import ProgressiveImage from "../progressive-image/ProgressiveImage";
import Loader from "../loader-spinner/Loader";

export default function Map({
                                children,
                                name,
                                image,
                                imageMin,
                                naturalSize,
                                defaultZoom = 1,
                                minZoom = 1,
                                maxZoom = 3,
                                defaultPosition,
                                targetZoom,
                                targetPosition,
                            }) {
    const mapRef = useRef();
    const mapBackgroundRef = useRef();
    const [markersPathStatus, setMarkersPathStatus] = useState("idle");
    const [showDistancesUserStatus, setShowDistancesUserStatus] = useState(true);
    const [routeDataExtended, setRouteDataExtended] = useState(null);
    const [zoom, setZoom] = useState(defaultZoom);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStart = useRef({ x: 0, y: 0 });
    const offsetStart = useRef({ x: 0, y: 0 });
    const [imageLoaded, setImageLoaded] = useState(false);

    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

    const clampOffset = (off, currentZoom = zoom) => {
        const container = mapRef.current;
        const imageEl = mapBackgroundRef.current;
        if (!container || !imageEl) return off;

        const { width: containerWidth, height: containerHeight } = container.getBoundingClientRect();

        const ratio = naturalSize.width / naturalSize.height;
        const containerRatio = containerWidth / containerHeight;

        let scaledWidth, scaledHeight;
        if (ratio < containerRatio) {
            scaledWidth = containerWidth * currentZoom;
            scaledHeight = containerWidth * 1/ratio * currentZoom;
        } else {
            scaledWidth = containerHeight * ratio * currentZoom;
            scaledHeight = containerHeight * currentZoom;
        }

        // Calculate bounds to prevent white space
        let minX, maxX, minY, maxY;

        if (scaledWidth <= containerWidth) {
            // If scaled image is smaller than container, center it
            minX = (containerWidth - scaledWidth) / 2;
            maxX = (containerWidth - scaledWidth) / 2;
        } else {
            // If scaled image is larger, allow panning but keep edges within bounds
            minX = containerWidth - scaledWidth; // Right edge can't go past container's left
            maxX = 0; // Left edge can't go past container's left
        }

        if (scaledHeight <= containerHeight) {
            // If scaled image is smaller than container, center it
            minY = (containerHeight - scaledHeight) / 2;
            maxY = (containerHeight - scaledHeight) / 2;
        } else {
            // If scaled image is larger, allow panning but keep edges within bounds
            minY = containerHeight - scaledHeight; // Bottom edge can't go past container's top
            maxY = 0; // Top edge can't go past container's top
        }

        // Ensure the bounds are applied correctly even when zoomed in
        return {
            x: Math.round(clamp(off.x, minX, maxX)),
            y: Math.round(clamp(off.y, minY, maxY)),
        };
    };

    const centerMap = (zoomLevel = zoom) => {
        const container = mapRef.current;
        const imageEl = mapBackgroundRef.current;
        if (!container || !imageEl) return;

        const { width: containerWidth, height: containerHeight } = container.getBoundingClientRect();
        const { width: imageWidth, height: imageHeight } = imageEl.getBoundingClientRect();

        let newOffset;
        if (defaultPosition) {
            newOffset = {
                x: containerWidth / 2 - (defaultPosition.x / naturalSize.width * imageWidth * zoomLevel),
                y: containerHeight / 2 - (defaultPosition.y / naturalSize.height * imageHeight * zoomLevel),
            };
        } else {
            newOffset = {
                x: (containerWidth - (imageWidth * zoomLevel)) / 2,
                y: (containerHeight - (imageHeight * zoomLevel)) / 2,
            };
        }

        setOffset(clampOffset(newOffset, zoomLevel));
    };

    const handleImageLoad = () => {
        console.log("Map image loaded:", image);
        centerMap();
        setImageLoaded(true);
    };

    const handleImageError = () => {
        console.error("Failed to load map image:", image);
        setImageLoaded(true); // Still set to true to remove the loading spinner
    };

    useEffect(() => {
        const mapContainer = mapRef.current;
        if (!mapContainer) return;

        const handleWheel = (e) => {
            e.preventDefault();
            const zoomFactor = 1.1;
            const newZoom = e.deltaY > 0
                ? Math.max(minZoom, zoom / zoomFactor)
                : Math.min(maxZoom, zoom * zoomFactor);

            const rect = mapContainer.getBoundingClientRect();
            const pointerX = e.clientX - rect.left;
            const pointerY = e.clientY - rect.top;
            const relativeX = (pointerX - offset.x) / zoom;
            const relativeY = (pointerY - offset.y) / zoom;

            const newOffsetX = pointerX - relativeX * newZoom;
            const newOffsetY = pointerY - relativeY * newZoom;

            setZoom(newZoom);
            setOffset(clampOffset({ x: newOffsetX, y: newOffsetY }, newZoom));
        };

        // mapContainer.addEventListener("wheel", handleWheel, { passive: false });
        return () => mapContainer.removeEventListener("wheel", handleWheel);
    }, [zoom, offset, minZoom, maxZoom]);

    const initialPinchDistanceRef = useRef(null);
    const initialZoomRef = useRef(zoom);
    const initialOffsetRef = useRef(offset);

    useEffect(() => {
        const mapContainer = mapRef.current;
        if (!mapContainer) return;

        const getDistance = (touch1, touch2) => {
            const dx = touch2.clientX - touch1.clientX;
            const dy = touch2.clientY - touch1.clientY;
            return Math.sqrt(dx * dx + dy * dy);
        };

        const getMidpoint = (touch1, touch2) => ({
            x: (touch1.clientX + touch2.clientX) / 2,
            y: (touch1.clientY + touch2.clientY) / 2,
        });

        const handleTouchStart = (e) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                initialPinchDistanceRef.current = getDistance(e.touches[0], e.touches[1]);
                initialZoomRef.current = zoom;
                initialOffsetRef.current = offset;
            }
        };

        const handleTouchMove = (e) => {
            if (e.touches.length === 2 && initialPinchDistanceRef.current) {
                e.preventDefault();
                const newDistance = getDistance(e.touches[0], e.touches[1]);
                const newZoom = Math.max(minZoom, Math.min(maxZoom,
                    initialZoomRef.current * (newDistance / initialPinchDistanceRef.current)));

                const mid = getMidpoint(e.touches[0], e.touches[1]);
                const relativeX = (mid.x - initialOffsetRef.current.x) / initialZoomRef.current;
                const relativeY = (mid.y - initialOffsetRef.current.y) / initialZoomRef.current;

                const newOffsetX = mid.x - relativeX * newZoom;
                const newOffsetY = mid.y - relativeY * newZoom;

                // setZoom(newZoom);
                // setOffset(clampOffset({ x: newOffsetX, y: newOffsetY }, newZoom));
            }
        };

        const handleTouchEnd = (e) => {
            if (e.touches.length < 2) initialPinchDistanceRef.current = null;
        };

        mapContainer.addEventListener("touchstart", handleTouchStart, { passive: false });
        mapContainer.addEventListener("touchmove", handleTouchMove, { passive: false });
        mapContainer.addEventListener("touchend", handleTouchEnd);
        mapContainer.addEventListener("touchcancel", handleTouchEnd);

        return () => {
            mapContainer.removeEventListener("touchstart", handleTouchStart);
            mapContainer.removeEventListener("touchmove", handleTouchMove);
            mapContainer.removeEventListener("touchend", handleTouchEnd);
            mapContainer.removeEventListener("touchcancel", handleTouchEnd);
        };
    }, [zoom, offset, minZoom, maxZoom]);

    const handleMouseDown = (e) => {
        e.preventDefault();
        setIsDragging(true);
        dragStart.current = { x: e.clientX, y: e.clientY };
        offsetStart.current = { ...offset };
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        const newOffset = { x: offsetStart.current.x + dx, y: offsetStart.current.y + dy };
        setOffset(clampOffset(newOffset));
    };

    const handleMouseUp = () => setIsDragging(false);

    const handleTouchStart = (e) => {
        if (e.touches.length === 1) {
            const touch = e.touches[0];
            setIsDragging(true);
            dragStart.current = { x: touch.clientX, y: touch.clientY };
            offsetStart.current = { ...offset };
        }
    };

    const handleTouchMove = (e) => {
        if (!isDragging || e.touches.length !== 1) return;
        const touch = e.touches[0];
        const dx = touch.clientX - dragStart.current.x;
        const dy = touch.clientY - dragStart.current.y;
        const newOffset = { x: offsetStart.current.x + dx, y: offsetStart.current.y + dy };
        setOffset(clampOffset(newOffset));
    };

    const handleTouchEnd = () => setIsDragging(false);

    const zoomStyles = {
        transform: `matrix(${zoom}, 0, 0, ${zoom}, ${offset.x}, ${offset.y})`,
        transformOrigin: "0px 0px 0px",
        transition: isDragging ? 'none' : 'transform 0.3s ease-out'
    };

    useEffect(() => {
        if (zoom === defaultZoom) {
            centerMap();
        }
    }, [zoom, defaultZoom]);

    const toggleZoom = () => {
        const container = mapRef.current;
        const imageEl = mapBackgroundRef.current;
        if (!container || !imageEl) return;

        const { width: containerWidth, height: containerHeight } = container.getBoundingClientRect();
        
        if (zoom !== defaultZoom) {
            // Zooming out to default
            setZoom(defaultZoom);
            // Just center the map completely when zooming out
            centerMap(defaultZoom);
        } else {
            // Zooming in
            const newZoom = targetZoom || 2;
            
            if (targetPosition) {
                // If we have a target position, zoom directly to that point
                const ratio = naturalSize.width / naturalSize.height;
                const containerRatio = containerWidth / containerHeight;
                
                // Calculate the size of the image at the new zoom level
                let scaledWidth, scaledHeight;
                if (ratio < containerRatio) {
                    scaledWidth = containerWidth * newZoom;
                    scaledHeight = containerWidth * (1/ratio) * newZoom;
                } else {
                    scaledWidth = containerHeight * ratio * newZoom;
                    scaledHeight = containerHeight * newZoom;
                }
                
                // Set the new zoom first
                setZoom(newZoom);
                
                // Calculate offset to center the target position
                const newOffset = {
                    x: Math.round(containerWidth / 2 - (targetPosition.x / naturalSize.width * scaledWidth)),
                    y: Math.round(containerHeight / 2 - (targetPosition.y / naturalSize.height * scaledHeight))
                };
                
                // Apply with clamping
                setOffset(clampOffset(newOffset, newZoom));
            } else {
                // Find where the center of the screen is pointing in the image
                const viewportCenterX = containerWidth / 2;
                const viewportCenterY = containerHeight / 2;
                
                // Convert to image coordinates
                const imagePointX = (viewportCenterX - offset.x) / zoom;
                const imagePointY = (viewportCenterY - offset.y) / zoom;
                
                // Set the new zoom first
                setZoom(newZoom);
                
                // Calculate the new offset needed to keep the same image point centered
                const newOffset = {
                    x: Math.round(viewportCenterX - (imagePointX * newZoom)),
                    y: Math.round(viewportCenterY - (imagePointY * newZoom))
                };
                
                // Apply with proper clamping
                setOffset(clampOffset(newOffset, newZoom));
            }
        }
    };

    return (
        <div
            className="map"
            ref={mapRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={imageMin ? {backgroundImage: `url(${imageMin})`} : {}}
        >
            <MapContextProvider value={{
                zoomStyles,
                markersPathStatus,
                setMarkersPathStatus,
                routeDataExtended,
                setRouteDataExtended,
                showDistancesUserStatus,
                setShowDistancesUserStatus,
                toggleZoom,
                zoom,
                defaultZoom
            }}>
                <ProgressiveImage
                    ref={mapBackgroundRef}
                    lowResSrc={imageMin || null}
                    highResSrc={image || null}
                    style={zoomStyles}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    onClick={() => {
                        const customEvent = new CustomEvent("mapImageClicked", {
                            detail: {message: "Map image was clicked", zoomed: zoom !== defaultZoom},
                        });
                        window.dispatchEvent(customEvent);
                    }}
                />
                {React.Children.map(children, (child) =>
                    React.isValidElement(child)
                        ? React.cloneElement(child, { mapName: name, naturalSize })
                        : child
                )}
                <Loader isVisible={!imageLoaded} nonBlocking={true} />
            </MapContextProvider>
        </div>
    );
}

Map.Clouds = Clouds;
Map.ZoomButton = ZoomButton;
Map.VisibilitySelect = VisibilitySelect;
Map.DistancesSwitch = DistancesSwitch;
Map.MarkersMapper = MarkersMapper;
Map.MarkersPath = MarkersPath;
Map.MarkersPathLoader = MarkersPathLoader;
Map.MarkersPathWindow = MarkersPathWindow;
